import React, { useEffect } from 'react';
import { Icon, Input, AutoComplete, Spin, Typography, Row, Col } from 'antd';
import { removeUnnecessaryCommas } from '../../helpers/common';
import { alertMessage } from '../../common/Common';
import Location from '../../../assets/images/Location_v2.svg';
import useAddressAutocomplete from '../../common/useAddressAutocomplete';

const { Option, OptGroup } = AutoComplete;
const { Text } = Typography;

// Admin/config address picker (orgs, accounts, locations, users, quotes,
// templates, agent, billing). Now delegates ALL autocomplete/session/details
// logic to the shared `useAddressAutocomplete` hook so the Places cost
// optimization (details-on-select) + `show_detailed_address_suggestions` org
// flag apply here too. Rendering, props and onSelect contract are unchanged.
// Note: warehouse-scoping props are intentionally NOT forwarded — this picker
// never scoped predefined results by warehouse, so predefined stays unscoped.
function AddressSearch(props) {
  const { hidePredefined = false, hideWebResults = false, doesAddressExist } = props;

  const {
    searchText,
    setSearchText,
    results: filteredOptions,
    loading,
    onSearch,
    reset,
    resolveSelection,
  } = useAddressAutocomplete({ hidePredefined, hideWebResults });

  useEffect(() => {
    if (!doesAddressExist) { reset(); }
  }, [doesAddressExist, reset]);

  const format_l_address = (a) => a?.l_address
    ? a.l_address.address_line1 + ", " + a.l_address.address_line2 + ", " + a.l_address.city + ", " + a.l_address.state + ", " + a.l_address.zipcode + ", " + a.l_address.country
    : "";
  // Readable label after selection: parsed address if present, else description.
  const rowLabel = (a) => removeUnnecessaryCommas(format_l_address(a) || a?.address || "");

  const handleSelect = async (value) => {
    const selected = (filteredOptions || []).find((address) => address.id === value);
    if (!selected) return;
    setSearchText(rowLabel(selected)); // immediate readable value
    // Cost-optimized flow: fetch Place Details only for the chosen web row.
    // No-op for predefined / detailed-mode rows that already carry l_address.
    const resolved = await resolveSelection(selected);
    // resolveSelection returns null when a web row's Place Details can't be
    // fetched — surface it instead of committing an address with no l_address.
    if (!resolved) {
      alertMessage(["Couldn't fetch address details. Please try again."], "error", 3);
      return;
    }
    props.onSelect(resolved);
    setSearchText(rowLabel(resolved));
  };

  const generateOptions = (addresses, formatFunction) =>
    addresses.map((address) => (
      <Option key={address.id} value={address.id}>
        <Row type='flex' gutter={10} align='middle' className='search_address_option_bar'>
          <Col>
            <img className='searchAddressLocation' src={Location} />
          </Col>
          <Col>
            <div><Text type='strong'>{address.name}</Text></div>
            <div><Text type="secondary">{formatFunction(address)}</Text></div>
          </Col>
        </Row>
      </Option>
    ));

  const predefinedStopsOptions = generateOptions(
    (filteredOptions || []).filter((address) => address.type === 'predefined'),
    format_l_address
  );
  const webResultsOptions = generateOptions(
    (filteredOptions || []).filter((address) => address.type === 'web'),
    (address) => address.address
  );

  return (
    <AutoComplete
      className="certain-category-search"
      dropdownClassName="certain-category-search-dropdown"
      dropdownMatchSelectWidth={false}
      dropdownStyle={{ width: 300 }}
      size="default"
      style={{ width: '100%', marginBottom: 10 }}
      value={searchText}
      dataSource={
        loading ? (
          [<Option key="loading" disabled><Spin /> Fetching addresses</Option>]
        ) : (
          [
            !hidePredefined && (
              <OptGroup key="predefined" label="Predefined">
                {predefinedStopsOptions.length === 0 ? (
                  <Option key="no-results" disabled>No results found</Option>
                ) : (
                  predefinedStopsOptions
                )}
              </OptGroup>
            ),
            !hideWebResults && (
              <OptGroup key="web" label="Web Results">
                {webResultsOptions.length === 0 ? (
                  <Option key="no-results" disabled>No results found</Option>
                ) : (
                  webResultsOptions
                )}
              </OptGroup>
            ),
          ].filter(Boolean)
        )
      }
      onChange={(value) => { if (!value || value.length === 0) { reset(); } }}
      onSearch={onSearch}
      onSelect={handleSelect}
      placeholder="Search addresses"
      optionLabelProp="value"
    >
      <Input suffix={<Icon type="search" className="certain-category-icon" />} />
    </AutoComplete>
  );
}

export default AddressSearch;
